/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Point, Rectangle } from 'pixi.js-legacy';
import { clamp } from 'utils/basic-utils';
import { Side, Ticket } from '@touchplan/common';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { LEVEL_OF_DETAIL_TABLE, LevelOfDetail } from '@appPlanApplication/lod/LevelOfDetail';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketType } from '@touchplan/common';
import { FancyText } from '@appCanvasCommon/fontpath/FancyText';
import { IFontOptions } from '@appCanvasCommon/fontpath/fontpath.interfaces';
import { setTicketWidthHelper } from 'src/app/utils/ticket-utils';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';

export interface TicketTextSettings {
	TASK_LEFT_MARGIN?: number;
	TASK_LEFT_MARGIN_PULL?: number;
	TASK_RIGHT_MARGIN?: number;
	TASK_RIGHT_MARGIN_PULL?: number;
	TASK_TOP_MARGIN?: number;
	TASK_TOP_MARGIN_PULL?: number;
	TASK_BOTTOM_MARGIN?: number;
	TASK_BOTTOM_MARGIN_PULL?: number;
	TASK_DRAWING_AREA_WIDTH?: number;
	TASK_DRAWING_AREA_WIDTH_PULL?: number;
	TASK_DRAWING_AREA_HEIGHT?: number;
	TASK_DRAWING_AREA_HEIGHT_PULL?: number;

	MILESTONE_LEFT_MARGIN?: number;
	MILESTONE_LEFT_MARGIN_PULL?: number;
	MILESTONE_RIGHT_MARGIN?: number;
	MILESTONE_RIGHT_MARGIN_PULL?: number;
	MILESTONE_TOP_MARGIN?: number;
	MILESTONE_TOP_MARGIN_PULL?: number;
	MILESTONE_BOTTOM_MARGIN?: number;
	MILESTONE_BOTTOM_MARGIN_PULL?: number;
	MILESTONE_DRAWING_AREA_WIDTH?: number;
	MILESTONE_DRAWING_AREA_WIDTH_PULL?: number;
	MILESTONE_DRAWING_AREA_HEIGHT?: number;
	MILESTONE_DRAWING_AREA_HEIGHT_PULL?: number;

	CONSTRAINT_LEFT_MARGIN?: number;
	CONSTRAINT_LEFT_MARGIN_PULL?: number;
	CONSTRAINT_RIGHT_MARGIN?: number;
	CONSTRAINT_RIGHT_MARGIN_PULL?: number;
	CONSTRAINT_TOP_MARGIN?: number;
	CONSTRAINT_TOP_MARGIN_PULL?: number;
	CONSTRAINT_BOTTOM_MARGIN?: number;
	CONSTRAINT_BOTTOM_MARGIN_PULL?: number;
	CONSTRAINT_DRAWING_AREA_WIDTH?: number;
	CONSTRAINT_DRAWING_AREA_WIDTH_PULL?: number;
	CONSTRAINT_DRAWING_AREA_HEIGHT?: number;
	CONSTRAINT_DRAWING_AREA_HEIGHT_PULL?: number;
}

export class TicketTextFontPath extends FancyText implements ComponentInterface {
	public override active = true;
	public override visible = true;
	public align = { x: false, y: false };
	public override maskArea: Rectangle = new Rectangle(0, 0, 1, 1);
	protected ticket: TicketContainer;
	public canvasAngularLand: CanvasAngularLand;
	constructor(
		fontFamily: string,
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		text: string = '',
		fontOptions: IFontOptions = { fontSize: 22, wordWrapOptions: {} }
	) {
		super(fontFamily, text, fontOptions);

		this.canvasAngularLand = canvasAngularLand;
		this.ticket = ticketContainer;
		this.tint = this.ticket.liveRoleTextColor;
		this.eventMode = EventMode.None;
	}

	public positionCenter() {
		const lastPos = new Point(this.x, this.y);
		// Get the center point of the text
		const textCenter: Point = new Point(this.widthPx / 2, this.heightPx / 2);
		// Get the center point of the maskArea
		const drawCenter: Point = new Point(
			this.maskArea.x + this.maskArea.width / 2,
			this.maskArea.y + this.maskArea.height / 2
		);
		if (this.align.x) {
			this.x = drawCenter.x - textCenter.x;
		}
		if (this.align.y) {
			this.y = clamp(drawCenter.y - textCenter.y, this.maskArea.y);
		}
		if (this.clipToMaskArea && !this.dirty) {
			this.dirty = lastPos.x !== this.x && lastPos.y !== this.y;
		}
	}

	protected _getHeightOffset(ticket: Ticket): number {
		let _offset = 0;
		const type = TicketUtils.getTicketType(ticket);
		if (ticket.view.location && type === TicketType.Task) {
			_offset += ticket.view.side === Side.PULL ? 40 : 25;
		}
		if (
			(ticket.view.liveStart || ticket.view.liveFinish) &&
			(LevelOfDetail.getLODLevel() >= LEVEL_OF_DETAIL_TABLE.MEDIUM || LevelOfDetail.isForced)
		) {
			_offset += ticket.view.side === Side.PULL ? 36 : 20;
		}

		return _offset;
	}

	/**
	 * Returs true if both Rectangles are equal.
	 * @param rect1
	 * @param rect2
	 */
	protected compareRects(rect1: Rectangle, rect2: Rectangle) {
		return (
			rect1.x === rect2.x &&
			rect1.y === rect2.y &&
			rect1.width === rect2.width &&
			rect1.height === rect2.height
		);
	}

	/**
	 * Applies offset and drawing area settings depending on the ticket type.
	 * @param {TicketContainer} ticketContainer
	 * @param {TicketTextSettings} settings
	 */
	protected applyOffsetAndDrawArea(ticketContainer: TicketContainer, settings: TicketTextSettings) {
		const lastRect = this.maskArea.clone();
		const data = ticketContainer.getData();
		const layout = ticketContainer.getLayout();
		const type = TicketUtils.getTicketType(data).toUpperCase();
		// Append a '_PULL' suffix if required.
		const pullSuffix = data.view.side === Side.PULL ? '_PULL' : '';

		// Set a width and height. This is so we can override the width or height from the ticket data.
		const width = settings[type + '_DRAWING_AREA_WIDTH' + pullSuffix] || layout.width;
		const height = settings[type + '_DRAWING_AREA_HEIGHT' + pullSuffix] || layout.height;

		const ticketWidth = setTicketWidthHelper(data, layout, this.canvasAngularLand.wideTicketSize$.getValue());

		// Set text offset (Changes depending on the active components
		this.wordWrapWidth =
			ticketWidth -
			settings[type + '_RIGHT_MARGIN' + pullSuffix] -
			settings[type + '_LEFT_MARGIN' + pullSuffix];
		this.maskArea.x = settings[type + '_LEFT_MARGIN' + pullSuffix];
		this.maskArea.y = settings[type + '_TOP_MARGIN' + pullSuffix];

		// Set text drawing area.
		this.maskArea.width = this.wordWrapWidth as number;
		this.maskArea.height = height - settings[type + '_BOTTOM_MARGIN' + pullSuffix];

		if (this.clipToMaskArea && !this.dirty) {
			this.dirty = !this.compareRects(lastRect, this.maskArea);
		}
	}

	/**
	 * When text changes, re-calculate the trig data.
	 * @param {string} newText
	 * @param {string} oldText
	 */
	public onTextChanged = (newText: string, oldText: string) => {
		this.setText(newText);
		this.calculateText();
	};
	/**
	 * Override this to change where the text data comes from
	 * @param text
	 */
	public setText(text: string) {
		if (!text) {
			text = '';
		}
		if (text === this._lastText) {
			return;
		}
		this.text = ''.concat(text);
		this.dirty = true;
	}

	public override destroy(options?): void {
		super.destroy(options);
	}
}
